import { useMediaUrl, useMediaRetry } from '../../components/Attachment/AttachmentMedia';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 동영상·오디오 미리보기 (2026-08-14).
 *
 * 채팅은 2026-07-12 부터 인라인 재생을 했는데(`AttachmentMedia`) 워크스페이스에는 없었다.
 * 같은 파일이 목록에서는 "미리보기를 지원하지 않는 형식" 이 되는 셈이다.
 *
 * 재생 자체는 브라우저가 한다 — MinIO presigned GET 이 **Range(206) 네이티브**라 프로그레시브
 * 스트리밍·seek 가 서버 변경 없이 된다(채팅에서 확인된 사실).
 *
 * ⚠ presign TTL 은 **5분**이다. 긴 영상을 보다가 seek 하면 403 이 난다. 그 복구 규약
 *   (error → fresh 서명 재발급 1회, 성공 재생마다 예산 복구)은 채팅과 **같은 훅을 그대로
 *   재사용**한다. 복제하면 한쪽만 고쳐진다.
 * ⚠ 자동재생하지 않는다(`preload="metadata"`) — 목록을 훑다 누른 것뿐인데 수십 MB 를 받게
 *   하지 않는다.
 */
export function WorkspaceMediaPreview({
  fileId, name, kind,
}: {
  fileId: string;
  name: string;
  kind: 'video' | 'audio';
}) {
  const { url, failed, reload } = useMediaUrl(fileId);
  const { hardFailed, onError, onLoadedData } = useMediaRetry(reload);

  if (failed || hardFailed) {
    return (
      <div className="workspace-docpanel-unsupported">
        {tWorkspace('workspace.editor.media_failed')}
      </div>
    );
  }
  if (!url) {
    return (
      <div className="workspace-docpanel-status" role="status">
        {tWorkspace('workspace.grid.loading')}
      </div>
    );
  }

  if (kind === 'audio') {
    return (
      <div className="workspace-docpanel-mediawrap">
        <audio
          className="workspace-docpanel-audio"
          controls
          preload="metadata"
          src={url}
          aria-label={name}
          onError={onError}
          onLoadedData={onLoadedData}
        />
      </div>
    );
  }

  return (
    <div className="workspace-docpanel-mediawrap">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- 사용자 업로드 미디어, 캡션 트랙 없음 */}
      <video
        className="workspace-docpanel-video"
        controls
        preload="metadata"
        playsInline
        src={url}
        aria-label={name}
        onError={onError}
        onLoadedData={onLoadedData}
      />
    </div>
  );
}
